import { useState, useEffect, useCallback } from 'react'
import { CheckCircle2, CreditCard, Banknote, Smartphone, Printer, Download } from 'lucide-react'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { Skeleton } from '@/components/ui/Skeleton'
import { usePOSOrder, useProcessPayment } from '@/lib/queries/usePOS'
import { useMyRestaurant } from '@/lib/queries/useRestaurants'
import { strapiPost } from '@/lib/strapi'
import { db } from '@/lib/db/database'
import { downloadReceiptHtml, printReceipt } from '@/lib/pos/receiptPrinter'
import { DEFAULT_POS_CURRENCY, formatPOSMoney, restaurantFromResponse } from '@/lib/pos/currency'
import { usePOSPrinterStore } from '@/lib/store/usePOSPrinterStore'
import { POSVisualNumpadField } from './POSVisualInput'
import { toast } from 'sonner'
import type { TenderType, POSCheckoutTotals, POSOrder } from '@/lib/types/pos'

interface Props {
  open: boolean
  onClose: () => void
  orderDocumentId: string
}

type TenderOption = { type: TenderType; label: string; icon: React.ElementType }

const TENDERS: TenderOption[] = [
  { type: 'cash', label: 'Cash', icon: Banknote },
  { type: 'card', label: 'Card', icon: CreditCard },
  { type: 'digital_wallet', label: 'Wallet', icon: Smartphone },
]

interface PaidSummary {
  amountPaid: number
  tipAmount: number
  taxTotal: number
  change: number
  tenderType: string
  tableName: string
}

export function POSCheckoutModal({ open, onClose, orderDocumentId }: Props) {
  const { data: orderResp } = usePOSOrder(orderDocumentId)
  const order = (orderResp as any)?.data as POSOrder | null | undefined
  const isUnsyncedOrder = !!orderDocumentId?.startsWith('temp-')
  const { data: restaurantResp } = useMyRestaurant()
  const restaurant = restaurantFromResponse(restaurantResp)
  const restaurantCurrency = (restaurant?.currency?.trim() || DEFAULT_POS_CURRENCY).toUpperCase()
  const printerMethod = usePOSPrinterStore((s) => s.method)
  const receiptWidth = usePOSPrinterStore((s) => s.receiptWidth)
  const autoPrintOnPayment = usePOSPrinterStore((s) => s.autoPrintOnPayment)
  const setLastPrinter = usePOSPrinterStore((s) => s.setLastPrinter)

  const [totals, setTotals] = useState<POSCheckoutTotals | null>(null)
  const [totalsLoading, setTotalsLoading] = useState(false)
  const [tender, setTender] = useState<TenderType>('cash')
  const [amount, setAmount] = useState('')
  const [tipPct, setTipPct] = useState<number | null>(null)
  const [customTip, setCustomTip] = useState('')
  const [paid, setPaid] = useState(false)
  const [paidSummary, setPaidSummary] = useState<PaidSummary | null>(null)
  const [printing, setPrinting] = useState(false)
  const displayCurrency = restaurantCurrency
  const money = useCallback(
    (amount: number, currency = displayCurrency) => formatPOSMoney(amount, currency),
    [displayCurrency],
  )

  const processPayment = useProcessPayment()
  const unweighedItems = (order?.orderItems ?? []).filter((item) => item.status !== 'voided' && item.weightIsEstimate === true)

  // Fetch server-computed totals on open; fall back to local computation when offline
  useEffect(() => {
    if (!open || !orderDocumentId) return
    setTotalsLoading(true)
    strapiPost<{ data: POSCheckoutTotals }>(`/api/pos/orders/${orderDocumentId}/checkout`, {})
      .then((res) => {
        const t = (res as any)?.data
        setTotals(t)
        setAmount(t?.balance?.toFixed(2) ?? '')
      })
      .catch(async () => {
        // Offline fallback: compute totals from Dexie order data
        const storedOrder = await db.posOrders.get(orderDocumentId)
        if (!storedOrder) {
          toast.error('Order data unavailable offline')
          setTotalsLoading(false)
          return
        }
        const items = storedOrder.orderItems?.filter((i) => i.status !== 'voided') ?? []
        const subtotal  = items.reduce((s, i) => s + i.unitPrice * i.quantity, 0)
        const taxTotal  = items.reduce((s, i) => s + i.unitPrice * i.quantity * (i.taxRate ?? 0), 0)
        const completed = storedOrder.payments?.filter((p) => p.status === 'completed') ?? []
        const totalPaid = completed.reduce((s, p) => s + p.amount, 0)
        const tipTotal  = completed.reduce((s, p) => s + (p.tipAmount ?? 0), 0)
        const balance   = Math.max(0, subtotal + taxTotal - totalPaid)
        const fallback: POSCheckoutTotals = { subtotal, taxTotal, tipTotal, totalPaid, balance, currency: restaurantCurrency }
        setTotals(fallback)
        setAmount(balance.toFixed(2))
      })
      .finally(() => setTotalsLoading(false))
  }, [open, orderDocumentId])

  // Reset on close
  useEffect(() => {
    if (!open) {
      setTotals(null)
      setAmount('')
      setTipPct(null)
      setCustomTip('')
      setPaid(false)
      setPaidSummary(null)
      setTender('cash')
    }
  }, [open])

  const balance = totals?.balance ?? 0
  const enteredAmount = parseFloat(amount) || 0

  // Fix tip calculation: round to nearest cent
  const tipAmount = (() => {
    if (customTip !== '') return parseFloat(customTip) || 0
    if (tipPct !== null) return Math.round(balance * (tipPct / 100) * 100) / 100
    return 0
  })()

  const change = tender === 'cash' && enteredAmount > balance ? enteredAmount - balance : 0

  const handleProcess = async () => {
    if (enteredAmount <= 0) return
    if (isUnsyncedOrder && tender !== 'cash') {
      toast.error('Only cash can be recorded while this order is offline')
      return
    }
    // For cash: applied amount is min(entered, balance); change is the excess
    const appliedAmount = tender === 'cash' ? Math.min(enteredAmount, balance) : enteredAmount
    const changeGiven   = tender === 'cash' ? Math.max(0, enteredAmount - balance) : 0
    const tip = tipAmount > 0 ? tipAmount : undefined

    // Capture summary before the call so success screen is accurate
    const summary: PaidSummary = {
      amountPaid: appliedAmount + (tip ?? 0),
      tipAmount:  tip ?? 0,
      taxTotal:   totals?.taxTotal ?? 0,
      change:     changeGiven,
      tenderType: tender,
      tableName:  order?.table?.name ?? '',
    }

    await processPayment.mutateAsync({
      orderDocumentId,
      amount: appliedAmount,
      tenderType: tender,
      tipAmount: tip,
      changeGiven: changeGiven > 0 ? changeGiven : undefined,
    })
    setPaidSummary(summary)
    setPaid(true)
    if (autoPrintOnPayment && order) {
      setTimeout(() => {
        handlePrint(order, summary).catch(() => undefined)
      }, 150)
    }
  }

  const handlePrint = async (receiptOrder = order, summary = paidSummary) => {
    if (!receiptOrder) return
    try {
      setPrinting(true)
      const label = await printReceipt({
        order: receiptOrder,
        restaurant,
        payment: summary,
        width: receiptWidth,
        method: printerMethod,
      })
      setLastPrinter(label)
      toast.success('Receipt sent to printer')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Receipt print failed')
    } finally {
      setPrinting(false)
    }
  }

  const handleDownload = () => {
    if (!order) return
    downloadReceiptHtml({
      order,
      restaurant,
      payment: paidSummary,
      width: receiptWidth,
    })
  }

  return (
    <Modal open={open} onClose={onClose} title="Checkout" size="lg">
      {paid && paidSummary ? (
        <div className="flex flex-col items-center py-8 gap-4 text-center">
          <CheckCircle2 className="h-16 w-16 text-[var(--color-accent-green)]" />
          <div>
            <p className="text-xl font-semibold text-[var(--color-text-primary)]">Order Paid</p>
            {paidSummary.tableName && (
              <p className="text-sm text-[var(--color-text-muted)] mt-1">
                Table {paidSummary.tableName} — {money(paidSummary.amountPaid)}
              </p>
            )}
            {paidSummary.tipAmount > 0 && (
              <p className="text-sm text-[var(--color-text-muted)]">Tip: {money(paidSummary.tipAmount)}</p>
            )}
            {paidSummary.taxTotal > 0 && (
              <p className="text-sm text-[var(--color-text-muted)]">Tax: {money(paidSummary.taxTotal)}</p>
            )}
            {paidSummary.change > 0 && (
              <p className="text-sm font-medium text-green-400 mt-1">Change: {money(paidSummary.change)}</p>
            )}
          </div>
          <div className="flex flex-wrap justify-center gap-2">
            <Button variant="secondary" onClick={() => handlePrint()} loading={printing} disabled={!order}>
              <Printer className="h-4 w-4" />
              Print receipt
            </Button>
            <Button variant="secondary" onClick={handleDownload} disabled={!order}>
              <Download className="h-4 w-4" />
              Download
            </Button>
            <Button onClick={onClose}>Close</Button>
          </div>
        </div>
      ) : (
        <div className="flex gap-6">
          {/* Bill summary */}
          <div className="flex-1 space-y-3">
            <p className="text-xs font-semibold text-[var(--color-text-muted)] uppercase tracking-wide">Order Summary</p>
            {!order ? (
              <div className="space-y-2">
                {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} variant="text" />)}
              </div>
            ) : (
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {order.orderItems?.map((item: any) => (
                  <div
                    key={item.documentId}
                    className={`flex justify-between text-sm ${item.status === 'voided' ? 'line-through opacity-40' : ''}`}
                  >
                    <span className="text-[var(--color-text-primary)]">
                      {item.weightGrams != null
                        ? `${item.weightGrams % 1000 === 0 ? `${item.weightGrams / 1000} kg` : `${item.weightGrams} g`} `
                        : `${item.quantity}× `}{item.menuItem?.name}
                      {item.variant ? ` (${item.variant.name})` : ''}
                      {item.weightIsEstimate === true && <span className="ml-1 text-[10px] font-semibold uppercase text-amber-400">estimate</span>}
                    </span>
                    <span className="text-[var(--color-text-secondary)] shrink-0 ml-2">
                      {money(item.unitPrice * item.quantity)}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {unweighedItems.length > 0 && (
              <p className="rounded-[var(--radius-md)] border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-300">
                Weigh {unweighedItems.map((item) => item.menuItem?.name).filter(Boolean).join(', ')} and confirm the actual grams in the order before taking payment.
              </p>
            )}

            {totalsLoading ? (
              <div className="space-y-1 pt-3 border-t border-[var(--color-border-subtle)]">
                {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} variant="text" />)}
              </div>
            ) : totals && (
              <div className="space-y-1 pt-3 border-t border-[var(--color-border-subtle)] text-sm">
                <div className="flex justify-between">
                  <span className="text-[var(--color-text-muted)]">Subtotal</span>
                  <span className="text-[var(--color-text-secondary)]">{money(totals.subtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--color-text-muted)]">Tax</span>
                  <span className="text-[var(--color-text-secondary)]">{money(totals.taxTotal)}</span>
                </div>
                {totals.tipTotal > 0 && (
                  <div className="flex justify-between">
                    <span className="text-[var(--color-text-muted)]">Tips</span>
                    <span className="text-[var(--color-text-secondary)]">{money(totals.tipTotal)}</span>
                  </div>
                )}
                {totals.totalPaid > 0 && (
                  <div className="flex justify-between">
                    <span className="text-[var(--color-text-muted)]">Paid</span>
                    <span className="text-[var(--color-text-secondary)]">{money(totals.totalPaid)}</span>
                  </div>
                )}
                <div className="flex justify-between pt-1 border-t border-[var(--color-border-subtle)]">
                  <span className="font-semibold text-[var(--color-text-primary)]">Balance Due</span>
                  <span className="font-bold text-lg text-[var(--color-accent-amber)]">{money(balance)}</span>
                </div>
              </div>
            )}
          </div>

          {/* Payment input */}
          <div className="w-52 space-y-4">
            <div>
              <p className="text-xs font-semibold text-[var(--color-text-muted)] uppercase tracking-wide mb-2">
                Tender
              </p>
              <div className="flex gap-1">
                {TENDERS.map(({ type, label, icon: Icon }) => (
                  <button
                    key={type}
                    onClick={() => setTender(type)}
                    className={[
                      'flex-1 flex flex-col items-center gap-1 py-2 rounded-[var(--radius-md)] border text-xs font-medium transition-colors',
                      tender === type
                        ? 'border-[var(--color-accent-amber)] bg-[var(--color-accent-amber)]/10 text-[var(--color-accent-amber)]'
                        : 'border-[var(--color-border-default)] text-[var(--color-text-secondary)] hover:border-[var(--color-border-strong)]',
                    ].join(' ')}
                  >
                    <Icon className="h-4 w-4" />
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <POSVisualNumpadField
                label="Amount"
                value={amount}
                onChange={setAmount}
                prefix={displayCurrency}
                quickValues={[
                  { label: 'Balance', value: balance.toFixed(2) },
                  { label: '+5', value: (enteredAmount + 5).toFixed(2) },
                  { label: '+10', value: (enteredAmount + 10).toFixed(2) },
                ]}
              />
            </div>

            {/* Tip — shown when amount covers balance */}
            {enteredAmount >= balance && balance > 0 && (
              <div>
                <p className="text-xs font-medium text-[var(--color-text-secondary)] mb-1">Tip</p>
                <div className="flex gap-1 mb-1">
                  {[10, 15, 20].map((pct) => (
                    <button
                      key={pct}
                      onClick={() => { setTipPct(tipPct === pct ? null : pct); setCustomTip('') }}
                      className={[
                        'flex-1 py-1.5 rounded-[var(--radius-sm)] text-xs font-medium border transition-colors',
                        tipPct === pct
                          ? 'border-[var(--color-accent-amber)] bg-[var(--color-accent-amber)]/10 text-[var(--color-accent-amber)]'
                          : 'border-[var(--color-border-default)] text-[var(--color-text-muted)]',
                      ].join(' ')}
                    >
                      {pct}%
                    </button>
                  ))}
                </div>
                <POSVisualNumpadField
                  value={customTip}
                  onChange={(value) => { setCustomTip(value); setTipPct(null) }}
                  placeholder={`Custom tip ${displayCurrency}`}
                  prefix={displayCurrency}
                  inputClassName="h-8 text-xs"
                />
              </div>
            )}

            {/* Cash change */}
            {tender === 'cash' && change > 0 && (
              <div className="rounded-[var(--radius-md)] bg-green-500/10 px-3 py-2 text-sm text-green-400 font-medium text-center">
                Change: {money(change)}
              </div>
            )}

            <Button
              className="w-full"
              onClick={handleProcess}
              loading={processPayment.isPending}
              disabled={!totals || enteredAmount <= 0 || unweighedItems.length > 0}
            >
              Process Payment
            </Button>
          </div>
        </div>
      )}
    </Modal>
  )
}
